import { ref, computed, readonly } from 'vue'
import { openModal } from '@/utils/modal'
import { MEMBER_UPGRADE_SUCCESS_MODAL_ID } from '@/constants/modalId'
import { type IUpgradeRefundAmountResponse } from '@/views/power/service'
import type { packageTimeEnum, vipPackage } from '@/assets/pay'

/**
 * 弹窗状态类型
 */
type ModalStatus = 'hidden' | 'visible' | 'closing'

/**
 * 会员升级成功弹窗属性接口
 */
export interface MemberUpgradeSuccessModalProps extends IUpgradeRefundAmountResponse {
  /** 是否自动续费 */
  isAutoRenewal?: boolean
  /** 目标会员套餐 */
  targetPackageKey?: keyof typeof vipPackage
  /** 目标套餐周期 */
  targetTimeMode?: keyof typeof packageTimeEnum
  /** 商品实际下发算力 */
  grantedPoints?: number
  /** 确认回调 */
  onConfirm?: () => void
  /** 关闭回调 */
  onClose?: () => void
}

/**
 * 会员升级成功弹窗组合式函数
 * @description 管理弹窗的打开、关闭和数据传递
 */
export const useMemberUpgradeSuccessModal = () => {
  // 弹窗状态
  const modalStatus = ref<ModalStatus>('hidden')
  
  // 当前弹窗数据
  const modalData = ref<Partial<MemberUpgradeSuccessModalProps>>({})

  // 是否显示弹窗
  const isVisible = computed(() => modalStatus.value === 'visible')

  /**
   * 打开升级成功弹窗
   * @param props - 弹窗属性
   */
  const openUpgradeSuccessModal = async (props: MemberUpgradeSuccessModalProps) => {
    modalData.value = props
    modalStatus.value = 'visible'

    const component = await import('./index.vue')
    openModal(MEMBER_UPGRADE_SUCCESS_MODAL_ID, {
      component: component.default,
      props: {...props} as unknown as Record<string, unknown>
    })
  }

  /**
   * 关闭弹窗
   */
  const closeModal = () => {
    modalStatus.value = 'closing'
    setTimeout(() => {
      modalStatus.value = 'hidden'
      modalData.value = {}
    }, 200) // 等待动画完成
  }

  /**
   * 处理确认
   * @param callback - 确认回调函数
   */
  const handleConfirm = (callback?: () => void) => {
    closeModal()
    callback?.()
  }

  /**
   * 处理关闭
   * @param callback - 关闭回调函数
   */
  const handleClose = (callback?: () => void) => {
    closeModal()
    callback?.()
  }

  /**
   * 快速打开升级成功弹窗的便捷方法
   * @param config - 升级成功配置
   */
  const showUpgradeSuccessModal = async (config: {
    refundInfo: IUpgradeRefundAmountResponse
    isAutoRenewal?: boolean
    targetPackageKey?: keyof typeof vipPackage
    targetTimeMode?: keyof typeof packageTimeEnum
    grantedPoints?: number
    onConfirm?: () => void
    onClose?: () => void
  }) => {
    try {
      await openUpgradeSuccessModal({
        ...config.refundInfo,
        isAutoRenewal: config.isAutoRenewal || false,
        targetPackageKey: config.targetPackageKey,
        targetTimeMode: config.targetTimeMode,
        grantedPoints: config.grantedPoints,
        onConfirm: () => handleConfirm(config.onConfirm),
        onClose: () => handleClose(config.onClose)
      })
    } catch (error) {
      console.error('打开会员升级成功弹窗失败:', error)
      throw error
    }
  }

  return {
    // 状态
    modalStatus: readonly(modalStatus),
    modalData: readonly(modalData),
    isVisible,

    // 方法
    openUpgradeSuccessModal,
    closeModal,
    handleConfirm,
    handleClose,
    showUpgradeSuccessModal
  }
}

// 创建全局实例
export const memberUpgradeSuccessModal = useMemberUpgradeSuccessModal()

// 导出便捷方法
export const showMemberUpgradeSuccessModal = memberUpgradeSuccessModal.showUpgradeSuccessModal
export const closeMemberUpgradeSuccessModal = memberUpgradeSuccessModal.closeModal
